import { useOutletContext } from "react-router-dom";
import { PostStage, HiringStatus, ServiceLevel } from "./types";
import { JobsOutletContext } from "./JobPostIndexLayout";

export const useJobPostContext = () => {
  const context = useOutletContext<JobsOutletContext>();
  const { data, loading, error, refetch } = context || {
    data: undefined,
    loading: false,
    error: "",
    refetch: () => {}
  };
  const jobPost = data?.jobPost;

  // refer to https://github.com/pgahq/pga-backend/blob/main/doc/tech-specs/2025-01-08-job-management.md#terminology for current def of closed job
  const isJobClosed = (currentJobPost = jobPost) => {
    if (!currentJobPost) return false;
    // A job is considered closed when it has INCOMPLETE status or FILLED status
    return [HiringStatus.FILLED, HiringStatus.INCOMPLETE].includes(currentJobPost.status as HiringStatus);
  };

  const isJobClosable = (currentJobPost = jobPost) => {
    if (!currentJobPost || isJobClosed(currentJobPost)) return false;

    // A job is closable if it's in an open stage (including Removed) or marked as Expired
    return (
      [PostStage.ACTIVE, PostStage.UNPOSTED, PostStage.REMOVED].includes(currentJobPost.stage as PostStage) ||
      currentJobPost.status === HiringStatus.EXPIRED
    );
  };

  const isJobActive = (currentJobPost = jobPost) => {
    if (!currentJobPost) return false;
    return currentJobPost.stage === PostStage.ACTIVE;
  };

  const isJobInReviewStage = (currentJobPost = jobPost) => {
    if (!currentJobPost || (currentJobPost.status as HiringStatus) === HiringStatus.EXPIRED) return false;
    const stage = currentJobPost.stage as PostStage;
    const serviceLevel = currentJobPost.serviceLevel as ServiceLevel;
    return (
      stage === PostStage.IN_REVIEW ||
      (stage === PostStage.DRAFT && serviceLevel === ServiceLevel.ASSISTED && !!currentJobPost.reviewRequestSent)
    );
  };

  const isJobDraft = (currentJobPost = jobPost) => {
    if (!currentJobPost || (currentJobPost.status as HiringStatus) === HiringStatus.EXPIRED) return false;
    const stage = currentJobPost.stage as PostStage;
    return stage === PostStage.DRAFT && !currentJobPost.reviewRequestSent;
  };

  const isJobFilledOrIncomplete = (currentJobPost = jobPost) => {
    if (!currentJobPost) return false;
    return [HiringStatus.FILLED, HiringStatus.INCOMPLETE].includes(currentJobPost.status as HiringStatus);
  };

  return {
    jobPost,
    isLoading: loading,
    error,
    refetch,
    isJobClosed,
    isJobClosable,
    isJobActive,
    isJobFilledOrIncomplete,
    isJobInReviewStage,
    isJobDraft
  };
};
